/**
 * One-row page header for desk pages: title + one-line context on the left,
 * the page's primary action(s) on the right. Wraps to two rows only when the
 * actions genuinely do not fit (phone).
 *
 *   <PageHeader
 *     title="Team Users"
 *     context="12 users · 12 of 50 staff seats used"
 *     actions={<button className="btn btn--primary">Invite user</button>}
 *   />
 */
import type { ReactNode } from 'react';

export interface PageHeaderProps {
  title: ReactNode;
  /** One line of counts / scope under the title. Keep it short. */
  context?: ReactNode;
  /** Right-aligned actions (buttons, links). Primary action last. */
  actions?: ReactNode;
  /** Optional element between the text and actions (e.g. a status pill). */
  meta?: ReactNode;
  /** Heading id, for `aria-labelledby` on the page section. */
  titleId?: string;
  className?: string;
}

export function PageHeader({
  title,
  context,
  actions,
  meta,
  titleId,
  className,
}: PageHeaderProps): JSX.Element {
  return (
    <header className={`page-header${className ? ` ${className}` : ''}`}>
      <div className="page-header__text">
        <h1 className="page-header__title" id={titleId}>
          {title}
        </h1>
        {context === undefined || context === null ? null : (
          <p className="page-header__context">{context}</p>
        )}
      </div>
      {meta === undefined || meta === null ? null : <div className="page-header__meta">{meta}</div>}
      {actions === undefined || actions === null ? null : (
        <div className="page-header__actions">{actions}</div>
      )}
    </header>
  );
}
